// DTO de D1 — Pulso del Día del directivo.
//
//   - GET /api/directivo/dashboard?cadencia=hoy|semana|mes
//
// El directivo ve TODAS las plantas del tenant (scope multi-planta vía
// usuarios_directivo_plantas), SOLO LECTURA. La pantalla es la cara comercial
// del producto (se proyecta en juntas), por eso prioriza claridad sobre densidad.
//
// Reutiliza cadenciaSchema y sparklinePuntoSchema de coordinador_dashboard para
// no duplicar contratos. El toggle visual "Hoy / 7 días / 30 días" mapea a
// cadencia hoy|semana|mes (mismo rango que C1).
//
// Los 4 KPIs del diseño:
//   - cumplimiento hoy (real / programa Alpina) + delta vs ayer
//   - producido hoy (paquetes) + nº plantas activas
//   - capacidad utilizada (real / meta_acumulada) + comparativo promedio mes
//   - incidencias activas (incidencias pendientes) + top-1 crítica
import { z } from 'zod';
import { cadenciaSchema, sparklinePuntoSchema } from './coordinador_dashboard.js';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
// =============================================================================
// Query
// =============================================================================
export const directivoDashboardQuerySchema = z.object({
    cadencia: cadenciaSchema.optional(),
});
// =============================================================================
// Estados (semáforo por planta + badge global)
// =============================================================================
export const estadoPlantaSchema = z.enum(['ok', 'warn', 'crit', 'sin_data']);
export const badgeEstadoGlobalSchema = z.enum(['operacion_estable', 'atencion', 'critico']);
// =============================================================================
// KPIs (4 cards del header)
// =============================================================================
const kpiCumplimientoSchema = z.object({
    /** Cumplimiento del periodo (0-100). null si no calculable. */
    valor_pct: z.number().min(0).nullable(),
    /** Delta en puntos porcentuales vs periodo anterior (ayer/sem ant/mes ant). */
    delta_pp: z.number().nullable(),
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
const kpiProduccionSchema = z.object({
    /** Paquetes producidos en el periodo. */
    cantidad: z.number().min(0),
    /** Plantas con captura en el periodo. */
    plantas_activas: z.number().int().min(0),
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
const kpiCapacidadSchema = z.object({
    /** Capacidad utilizada del periodo (real / meta_acumulada × 100). null si no calculable. */
    utilizada_pct: z.number().min(0).nullable(),
    /** Promedio del mes en curso (para comparativo "vs 78% promedio mes"). */
    promedio_mes_pct: z.number().min(0).nullable(),
    /** Delta vs promedio mes en pp. */
    delta_pp: z.number().nullable(),
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
const kpiIncidenciasSchema = z.object({
    /** Incidencias pendientes en el scope. */
    count_total: z.number().int().min(0),
    /** De las pendientes, cuántas son críticas. */
    count_critica: z.number().int().min(0),
    /** Top-1 crítica para el subtítulo "1 crítica · Sopó V3". null si no hay. */
    critica_top: z
        .object({
        planta_nombre: z.string(),
        /** Código de línea afectada, o null si la incidencia es a nivel planta. */
        linea_label: z.string().nullable(),
    })
        .nullable(),
    sparkline: z.array(sparklinePuntoSchema).max(7),
});
export const kpisDirectivoSchema = z.object({
    cumplimiento: kpiCumplimientoSchema,
    produccion: kpiProduccionSchema,
    capacidad: kpiCapacidadSchema,
    incidencias: kpiIncidenciasSchema,
});
// =============================================================================
// Planta (alimenta marcadores del mapa + cards de "Resumen por planta")
// =============================================================================
export const plantaPulsoSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    /** Departamento / ciudad (de plantas.direccion). null si no cargado. */
    departamento: z.string().nullable(),
    /** Geo para posicionar el marcador. null si la planta no tiene coords. */
    latitud: z.number().nullable(),
    longitud: z.number().nullable(),
    estado: estadoPlantaSchema,
    /** Número del turno corriendo ahora en esa planta. null si fuera de turno. */
    turno_activo_numero: z.number().int().min(1).max(4).nullable(),
    lineas_activas: z.number().int().min(0),
    /** Cumplimiento hoy (programa Alpina). null si sin data. */
    cumplimiento_pct: z.number().min(0).nullable(),
    /** Paquetes producidos hoy. */
    producido: z.number().min(0),
    incidencias_activas: z.number().int().min(0),
    incidencias_criticas: z.number().int().min(0),
});
// =============================================================================
// Pérdida por paros del período — "la plata" (estilo OEE, misma fórmula que C6:
// Σ minutos_perdidos × capacidad_estandar_unidades_hora / 60, a nivel paro).
// =============================================================================
export const perdidaParoDirectivoSchema = z.object({
    /** Minutos totales de paro del período (paros cerrados, registros válidos). */
    minutos: z.number().min(0),
    /** Unidades que se dejaron de producir por esos paros. */
    unidades_perdidas: z.number().min(0),
    /** Top 3 causas madre por unidades perdidas. */
    top_causas: z
        .array(z.object({
        causal: z.string(),
        minutos: z.number().min(0),
        unidades: z.number().min(0),
    }))
        .max(3),
    /** DN1: paros ABIERTOS del período (eventos distintos) — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
});
// =============================================================================
// Narrativa del header
// =============================================================================
export const narrativaDirectivoSchema = z.object({
    /** Primer nombre del directivo. */
    saludo: z.string(),
    /** "4 plantas · 87% del programa hoy · 1 planta con incidencia crítica". */
    linea_estado: z.string(),
    badge_estado: badgeEstadoGlobalSchema,
});
// =============================================================================
// Atajos a las otras cadencias (3 cards inferiores)
// =============================================================================
export const atajoCadenciaSchema = z.object({
    clave: z.enum(['resumen_semanal', 'analisis_mensual', 'analisis_cliente']),
    /** Etiqueta caps superior ("PARA REUNIÓN DEL VIERNES"). */
    eyebrow: z.string(),
    titulo: z.string(),
    descripcion: z.string(),
    /** Dato vivo resumen ("87% promedio · +5% vs sem pasada"). */
    dato_resumen: z.string(),
    link: z.string(),
});
// =============================================================================
// Response GET /api/directivo/dashboard
// =============================================================================
export const directivoDashboardSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_total: z.number().int().min(0),
    }),
    fecha_hoy: z.string().date(),
    /**
     * DÍA OPERATIVO del pulso: el día de negocio en curso. En la madrugada
     * (cola post-medianoche del T3 + gracia) es fecha_hoy - 1 — el turno que
     * está corriendo pertenece a ayer. Todos los agregados "hoy" usan ESTA
     * fecha, no el calendario.
     */
    fecha_operativa: z.string().date(),
    hora_servidor: z.string().regex(horaTimeRegex),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Turno corriendo AHORA (tenant-wide). null = fuera de turno. */
    turno_actual_id: z.string().uuid().nullable(),
    cadencia: cadenciaSchema,
    narrativa: narrativaDirectivoSchema,
    kpis: kpisDirectivoSchema,
    /** Pérdida por paros del período (la plata). */
    perdida_paro: perdidaParoDirectivoSchema,
    /** Una entrada por planta del scope (mapa + cards). Orden por código. */
    plantas: z.array(plantaPulsoSchema),
    /** Atajos a Resumen Semanal / Análisis Mensual / Análisis por Cliente. */
    atajos: z.array(atajoCadenciaSchema),
    /** ISO 8601 del momento de cálculo (para "Actualizado hace X"). */
    actualizado_at: z.string().datetime(),
});
//# sourceMappingURL=directivo_dashboard.js.map